import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didUpdate } from '@ember/render-modifiers';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PrismInputSignature } from './input.gts';
import PrismInput from './input.gts';

function triggerInputEvent(
  value: string,
  inputElement: HTMLInputElement,
): void {
  if (inputElement.value === value) {
    return;
  }

  inputElement.value = value;

  const event = new Event('input');
  inputElement.dispatchEvent(event);
}

// These arguments are simply passed on to the Input component
type PrismInputArgs = Pick<
  PrismInputSignature['Args'],
  | 'name'
  | 'placeholder'
  | 'label'
  | 'required'
  | 'requiredErrorMessage'
  | 'hasErrors'
  | 'errorMessage'
  | 'hasWarnings'
  | 'warningMessage'
  | 'isClearable'
  | 'disabled'
  | 'autofocus'
  | 'removePadding'
  | 'isTouched'
  | 'iconAfter'
  | 'inlineLabel'
  | 'isSmall'
  | 'tooltip'
  | 'helpText'
  | 'maxLength'
  | 'readonly'
  | 'onClearInput'
  | 'onKeyUp'
  | 'onFocusIn'
  | 'onFocusOut'
  | 'onClick'
>;

type Step = 0.000001 | 0.01 | 1;

export interface PrismNumberInputSignature {
  Args: PrismInputArgs & {
    locale?: string;
    max?: number;
    min?: number;
    onValueChange: (value: number | null) => unknown;
    step?: Step;
    value: number | null | undefined;
  };
  Element: PrismInputSignature['Element'];
}

export default class PrismNumberInput extends Component<PrismNumberInputSignature> {
  @tracked internalValue = this.valueToString(this.args.value);

  get decimalPrecision(): number {
    const { step } = this;

    if (step === undefined) {
      return 0;
    }

    return -1 * Math.log10(step);
  }

  get decimalSeparator(): '.' | ',' {
    const numFormatter = new Intl.NumberFormat(
      this.args.locale ?? navigator.language,
    );
    const parts = numFormatter.formatToParts(1.1);
    const decimalSeparator = parts.find(
      (part) => part.type === 'decimal',
    )?.value;

    return decimalSeparator === ',' ? ',' : '.';
  }

  get isOnlyPositiveAllowed(): boolean {
    return typeof this.args.min === 'number' && this.args.min >= 0;
  }

  get pattern(): RegExp {
    const OPTIONAL_MINUS_SIGN = '-?';
    const INTEGER_PATTERN = '[0-9]*';
    const DECIMAL_PATTERN = `[0-9]*[.,]?[0-9]{0,${this.decimalPrecision}}`;

    const areDecimalsAllowed = this.decimalPrecision > 0;

    const prefix = this.isOnlyPositiveAllowed ? '' : OPTIONAL_MINUS_SIGN;
    const numberPattern = areDecimalsAllowed
      ? DECIMAL_PATTERN
      : INTEGER_PATTERN;

    return new RegExp(`^${prefix}${numberPattern}$`);
  }

  get step(): Step {
    return this.args.step ?? 1;
  }

  clampValue(value: number): number {
    let clampedValue = value;

    const { min, max, step } = this.args;
    if (typeof min === 'number' && value < min) {
      clampedValue = min;
    }

    if (typeof max === 'number' && value > max) {
      clampedValue = max;
    }

    if (typeof step === 'number') {
      clampedValue = Math.round(clampedValue / step) * step;
    }

    return clampedValue;
  }

  decrement(): number {
    let value = this.parseValue(this.internalValue);
    if (Number.isNaN(value)) {
      value = 0;
    }

    return this.clampValue(value - this.step);
  }

  increment(): number {
    let value = this.parseValue(this.internalValue);
    if (Number.isNaN(value)) {
      value = 0;
    }

    return this.clampValue(value + this.step);
  }

  parseValue(value?: string): number {
    if (!value || value === '-') {
      return NaN;
    }

    return parseFloat(value.replace(',', '.'));
  }

  // Also supports converting scientific notation to normal string (e.g. 1e+21 to 1000000000000000000000)
  valueToString(value: number | null | undefined): string {
    if (value === undefined || value === null) {
      return '';
    }

    const localeOptions = {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    };

    if (this.decimalPrecision > 0) {
      localeOptions.minimumFractionDigits = this.decimalPrecision;
      localeOptions.maximumFractionDigits = this.decimalPrecision;
    }

    return value
      .toLocaleString('fullwide', localeOptions)
      .replace('.', this.decimalSeparator);
  }

  @action
  handleBlur(event: Event): void {
    const numberValue = this.parseValue(this.internalValue);
    const value = Number.isNaN(numberValue)
      ? ''
      : this.valueToString(this.clampValue(numberValue));

    const input = event.target as HTMLInputElement;
    triggerInputEvent(value, input);
  }

  @action
  handleInput(event: Event): void {
    const { value } = event.target as HTMLInputElement;

    // Allows a standalone minus sign (only if negative values are allowed), and empty values
    if (
      value === '' ||
      (value === '-' && !this.isOnlyPositiveAllowed) ||
      this.pattern.test(value)
    ) {
      const parsedValue = this.parseValue(value);
      this.args.onValueChange(Number.isNaN(parsedValue) ? null : parsedValue);
      this.internalValue = value;

      return;
    }

    // Prevent the internal input value and component value from getting out of sync
    (event.target as HTMLInputElement).value = this.internalValue;
  }

  @action
  handleKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    const target = event.target as HTMLInputElement;

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const value = this.valueToString(this.increment());
      triggerInputEvent(value, target);
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const value = this.valueToString(this.decrement());
      triggerInputEvent(value, target);
    }
  }

  /**
   * Allows updating the input value from the parent, if the _parsed_ value is different.
   * We only check equality on parsed values, because we don't want the input to be updated
   * from a parent component while typing if the resulting value would be the same
   */
  @action
  setInternalValue(_: unknown, [value]: [number | null | undefined]): void {
    // Special handling for minus sign - don't overwrite if we have a minus sign and parent passes null
    if (this.internalValue === '-' && value === null) {
      return;
    }

    // Only update if parsed values differ to avoid disrupting typing
    if (this.parseValue(this.internalValue) !== value) {
      this.internalValue = this.valueToString(value);
    }
  }

  <template>
    <PrismInput
      @autocomplete="off"
      @autofocus={{@autofocus}}
      @disabled={{@disabled}}
      @errorMessage={{@errorMessage}}
      @hasErrors={{@hasErrors}}
      @hasWarnings={{@hasWarnings}}
      @helpText={{@helpText}}
      @iconAfter={{@iconAfter}}
      @inlineLabel={{@inlineLabel}}
      @inputmode="numeric"
      @isClearable={{@isClearable}}
      @isSmall={{@isSmall}}
      @isTouched={{@isTouched}}
      @label={{@label}}
      @maxLength={{@maxLength}}
      @name={{@name}}
      @onBlur={{this.handleBlur}}
      @onClearInput={{@onClearInput}}
      @onClick={{@onClick}}
      @onFocusIn={{@onFocusIn}}
      @onFocusOut={{@onFocusOut}}
      @onInput={{this.handleInput}}
      @onKeyDown={{this.handleKeyDown}}
      @onKeyUp={{@onKeyUp}}
      @placeholder={{@placeholder}}
      @readonly={{@readonly}}
      @removePadding={{@removePadding}}
      @required={{@required}}
      @requiredErrorMessage={{@requiredErrorMessage}}
      @tooltip={{@tooltip}}
      @value={{this.internalValue}}
      @warningMessage={{@warningMessage}}
      {{didUpdate this.setInternalValue @value}}
      data-test-number-input={{@label}}
      ...attributes
    />
  </template>
}
